import React, { ChangeEvent, useState, useEffect } from "react";
import styles from "@/styles/desktop/Form.module.scss";
import { Input, Select } from "./Fields";
import {
	options,
	useHandleCellPhoneChange,
	useHandleEmailChange,
	useHandleFullNameChange,
	useHandleSelectedTeamChange,
	useHandleSelectedUnitChange,
	typeOptions,
	useHandleTypeChange,
} from "@/app/utils/formData";
import { FormProps, FormStateProps, Option } from "@/app/interface/components";
import handleSubmit from "@/app/services/handleSubmit";
import { useRouter } from "next/router";

function getModelName(urlParams: URLSearchParams): string {
	const machine = urlParams.get("machine") || "";
	const model = urlParams.get("model") || "";

	if (!machine) {
		return "";
	}

	const fullModel = `${machine} ${model}`
		.replace("seeders", "Plantadeira")
		.replace("sprayers", "Pulverizador")
		.replace("harvester", "Colheitadeira")
		.replace("tractors", "Trator")
		.replace(/-/g, " ")
		.replace("cana de acucar", "Cana-de-açúcar")
		.replace("t4 75s", "t4.75s")
		.replace("t5 110s", "t5.110s")
		.toUpperCase();

	return fullModel;
}

const FormDesktop: React.FC<FormProps> = ({
	consorcio,
	financing,
	about,
	support,
	parts,
}) => {
	const router = useRouter();
	const { asPath } = router;
	const params = asPath.split("?")[1] ?? "";
	const urlParams = new URLSearchParams(params);
	const model = getModelName(urlParams);

	const [state, setState] = useState<FormStateProps>({
		selectedType: "",
		isSelectedTypeValid: true,
		fullName: "",
		isFullNameValid: true,
		email: "",
		isEmailValid: true,
		phone: "",
		isPhoneValid: true,
		selectedTeam: null,
		isSelectedTeamValid: true,
		unitOptions: { labels: [], values: [] },
		selectedUnit: null,
		isSelectedUnitValid: true,
	});

	const [isFormValid, setIsFormValid] = useState(false);

	useEffect(() => {
		const isFormFilledAndValid =
			Boolean(state.fullName) &&
			state.isFullNameValid &&
			Boolean(state.email) &&
			state.isEmailValid &&
			Boolean(state.phone) &&
			state.isPhoneValid &&
			Boolean(state.selectedTeam) &&
			state.isSelectedTeamValid &&
			Boolean(state.selectedUnit) &&
			state.isSelectedUnitValid &&
			Boolean(state.selectedType) &&
			state.isSelectedTypeValid;

		setIsFormValid(isFormFilledAndValid);
	}, [state]);

	const teamOptions = options.map((opt: Option) => ({
		value: opt.team,
		label: opt.team,
	}));
	const unitOptions = state.unitOptions.labels.map(
		(label: string, index: number) => ({
			label,
			value: state.unitOptions.values[index],
		})
	);

	const handleFullNameChange = useHandleFullNameChange();
	const handleEmailChange = useHandleEmailChange();
	const handleCellPhoneChange = useHandleCellPhoneChange();
	const handleTeamChange = useHandleSelectedTeamChange();
	const handleUnitChange = useHandleSelectedUnitChange();
	const handleTypeChange = useHandleTypeChange();

	return (
		<div className={styles.main}>
			<div className={styles.content}>
				<h2 className="h2-bold">Atendimento</h2>
				<form
					className={styles.form}
					onSubmit={(e) =>
						handleSubmit(
							e,
							params,
							model,
							consorcio!,
							financing!,
							about!,
							support!,
							parts!,
							state.selectedTeam!,
							state.selectedUnit!,
							Number(state.selectedType)
						)
					}
				>
					<Input
						label={"Nome e sobrenome"}
						type={"text"}
						name={"fullName"}
						placeholder=" "
						value={state.fullName}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							handleFullNameChange(e, setState)
						}
						isValid={state.isFullNameValid}
						errorMessage="*Nome inválido"
					/>

					<Input
						label="E-mail"
						type="text"
						name="email"
						placeholder=" "
						value={state.email}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							handleEmailChange(e, setState)
						}
						isValid={state.isEmailValid}
						errorMessage="*E-mail inválido"
					/>

					<Input
						label="(00) 00000-0000"
						type="text"
						name="phone"
						placeholder=" "
						value={state.phone}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							handleCellPhoneChange(e, setState)
						}
						isValid={state.isPhoneValid}
						errorMessage="*Telefone inválido"
					/>

					<div className={styles.selectContainer}>
						<Select
							ariaLabel="Selecione um estado"
							selectedValue={state.selectedTeam}
							setSelectedValue={handleTeamChange}
							resetValue={handleUnitChange}
							isValid={state.isSelectedTeamValid}
							errorMessage="Opção inválida"
							options={teamOptions}
							setState={setState}
						/>

						<Select
							ariaLabel="Selecione uma unidade"
							selectedValue={state.selectedUnit}
							setSelectedValue={handleUnitChange}
							isValid={state.isSelectedUnitValid}
							errorMessage="Unidade inválida"
							options={unitOptions}
							setState={setState}
						/>
					</div>

					<Select
						ariaLabel="Selecione o tipo de contato"
						selectedValue={state.selectedType}
						setSelectedValue={handleTypeChange}
						isValid={state.isSelectedTypeValid}
						errorMessage="Tipo de contato inválido"
						options={typeOptions}
						setState={setState}
					/>

					<span className="defesa">
						Ao clicar em enviar você autoriza que façamos contato com você e o
						tratamento dos seus dados para envio de informações comerciais
						personalizadas, conforme as disposições do Programa de Privacidade.
					</span>

					<div className={styles.btn}>
						<button
							type="submit"
							className="btn-primary"
							disabled={!isFormValid}
						>
							Enviar
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default FormDesktop;
